<template>
  <div class="flex-1 font-light">
    <h2 v-if="!hideOrderId" class="ui-text-l mb-4 text-gray-about">
      {{ `${$t("order")} #${orderData.po_number}` }}
    </h2>

    <h2 class="ui-text-l mb-1 text-gray-about">
      {{ $t("paymentMethod") }}
    </h2>

    <div class="mb-4.5 space-y-1">
      <div class="mb-0.5 flex justify-between">
        <div class="text-gray-about">
          {{
            Number(orderData.final_amount) === 0
              ? orderData.payment_method
              : orderData.payment_method_name
          }}
        </div>
        <div v-if="orderData.user_name" class="text-gray-about">
          {{ orderData.user_name }}
        </div>
      </div>
      <div class="flex justify-between">
        <div v-if="generalStore.isAuthenticated" class="text-gray-about">
          <template v-if="showPaymentDots">
            {{ `•••••••••• ${orderData.payment_method_last4}` }}
          </template>
          <template v-else>
            <template v-if="showLast4">
              {{ orderData.payment_method_last4 }}
            </template>
          </template>
        </div>
        <div v-else></div>
        <div class="max-w-[150px] text-end text-gray-about">
          {{ orderData.billing_address }}
        </div>
      </div>
    </div>
    <h2 class="ui-text-l mb-1 text-gray-about">
      {{ $t("orderSummary") }}
    </h2>
    <div class="space-y-1">
      <div class="flex justify-between">
        <div class="text-gray-about">
          {{ $t("subtotal") }}
        </div>
        <div class="text-gray-about">
          {{ $n(Number(orderData.base_amount), "currencyCents") }}
        </div>
      </div>
      <div class="flex justify-between">
        <div class="text-gray-about">
          {{ $t("shipping") }}
        </div>

        <div class="text-gray-about">
          {{
            Number(orderData.shipping_amount) > 0
              ? $n(Number(orderData.shipping_amount), "currencyCents")
              : $t("free")
          }}
        </div>
      </div>

      <div
        v-for="discountItem in discountItems"
        :key="discountItem.code"
        class="mb-2 flex justify-between"
      >
        <div class="text-gray-about">
          {{ $t("promoCode") }}
        </div>

        <div class="text-gray-about">
          {{ getDiscountValue(discountItem) }}
        </div>
      </div>

      <div
        v-for="giftCardItem in giftCardItems"
        :key="giftCardItem.code"
        class="mb-2 flex justify-between"
      >
        <div class="text-gray-about">
          {{ $t("giftCardAmount") }}
        </div>

        <div class="text-gray-about">
          -{{ $n(Number(giftCardItem.used_gift_card_amount), "currencyCents") }}
        </div>
      </div>

      <div class="flex justify-between">
        <div class="text-gray-about">{{ $t("tax") }}</div>
        <div class="text-gray-about">
          {{ $n(Number(orderData.tax_amount), "currencyCents") }}
        </div>
      </div>
    </div>
    <div class="my-4.5 h-px bg-gray-about/20 lg:bg-gray-about" />
    <div class="flex justify-between">
      <div class="text-gray-about">
        {{ $t("totalBilled") }}
      </div>
      <div class="text-gray-about">
        {{ $n(Number(orderData.final_amount), "currencyCents") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { OrderData } from "ecom-types";

import { paymentMethods } from "@/configs";

const props = defineProps<{ orderData: OrderData; hideOrderId?: boolean }>();

const generalStore = useGeneralStore();
const { getDiscountValue } = useCart();

const showPaymentDots = computed(
  () =>
    ![
      paymentMethods.paypal,
      paymentMethods.affirm,
      paymentMethods.affirm_express,
      paymentMethods.googlePay,
      paymentMethods.applePay,
      paymentMethods.paytomorrow,
    ].includes(props.orderData.payment_method_code) &&
    Number(props.orderData.final_amount) > 0,
);

const showLast4 = computed(
  () =>
    ![paymentMethods.googlePay, paymentMethods.applePay].includes(
      props.orderData.payment_method_code,
    ) && Number(props.orderData.final_amount) > 0,
);

const discountItems = computed(() => {
  if (props.orderData?.options?.discounts) {
    return Object.values(props.orderData.options.discounts);
  }
  return [];
});

const giftCardItems = computed(() => {
  if (props.orderData?.options?.gift_cards) {
    return Object.values(props.orderData.options.gift_cards);
  }
  return [];
});
</script>
